// =============================================================================
// screens/NotificationsScreen.tsx — the notification feed (bare-RN P3-8 port).
//
// A8's pills / Today-Week-Earlier grouping / swipe-to-read / blocked-actor hide /
// per-filter unread badges, all preserved. Feed data is REST + the live socket
// (onNotification prepends; onNotificationUnreadCount keeps counts honest).
//
// ── BARE-RN MIGRATION (blueprint §1/§2) ──────────────────────────────────────
//   • expo-router useRouter()      → react-navigation useNavigation(); row taps
//     router.push('/post/[id]')    → navigation.navigate('Post',{id}),
//     router.push('/user/[u]')     → navigation.navigate('UserProfile',{username}).
//   • expo-router <Stack.Screen>   → the native RootStack header for this route is
//     hidden (App.tsx headerShown:false); we render an IN-SCREEN translucent
//     GlassSurface header (back chevron + title + "Mark all read") so the
//     ParticleBackground shows through the status bar (Apple skill §12).
//   • @expo/vector-icons Ionicons  → components/ui/Icon (Ionicons shim, IoniconName).
//   • No expo-notifications here — the feed is fully socket-driven; the app-icon
//     BADGE lives in components/notifications/InAppNotification (P4 native setter).
// =============================================================================
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  SectionList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Swipeable, GestureHandlerRootView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, type IoniconName } from '../components/ui/Icon';
import { api } from '../services/api';
import { colors, spacing, radius, fonts, typographyScale, getLayout, layout as layoutTokens } from '../constants/theme';
import ParticleBackground from '../components/ui/ParticleBackground';
import ScreenTip from '../components/ui/ScreenTip';
import { GlassCard, GlassSurface, GlassButton, GlassPill, GlassPillGroup } from '../components/glass';
import Asset3D from '../components/ui/Asset3D';
import ComingSoonBadge from '../components/ComingSoonBadge';
import { gateOr, SHOW_CRYPTO } from '../lib/comingSoon';
import { isBlocked, onBlockedChange, isMuted, onMutedChange } from '../services/moderation';
import { onNotification } from '../services/socket';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList, 'Notifications'>;

type FilterKey = 'all' | 'likes' | 'comments' | 'follows' | 'mentions';

interface Notification {
  id: string;
  type: 'like' | 'comment' | 'follow' | 'mention' | 'repost' | 'dm';
  read: boolean;
  message: string;
  actor: {
    username: string;
    displayName: string;
    avatar: string;
  };
  postId?: string;
  conversationId?: string;
  createdAt: string;
}

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'follows', label: 'Follows' },
  { key: 'mentions', label: 'Mentions' },
];

const TYPE_ICONS: Record<string, { name: IoniconName; color: string }> = {
  like: { name: 'heart', color: colors.error },
  comment: { name: 'chatbubble', color: colors.info },
  follow: { name: 'person-add', color: colors.primary },
  mention: { name: 'at', color: colors.warning },
  repost: { name: 'repeat', color: colors.success },
  crypto_tip: { name: 'cash', color: colors.success },
  community_post: { name: 'people', color: colors.primary },
  dm: { name: 'mail', color: colors.info },
  system: { name: 'megaphone', color: colors.warning },
};

// Backend Notification rows are raw: { id, type (UPPERCASE enum), title,
// content, data (Json?), isRead, createdAt }. There is no actor/message/read/
// postId — map enum -> the client's lowercase bucket, isRead->read,
// content->message, and best-effort pull actor/postId from the `data` JSON.
function normalizeNotificationType(type?: string): Notification['type'] {
  switch ((type || '').toUpperCase()) {
    case 'LIKE':
    case 'POST_LIKE':
      return 'like';
    case 'COMMENT':
    case 'POST_COMMENT':
    case 'REPLY':
      return 'comment';
    case 'FOLLOW':
    case 'FRIEND_REQUEST':
      return 'follow';
    case 'MENTION':
      return 'mention';
    case 'CRYPTO_TIP':
      return 'crypto_tip' as Notification['type'];
    case 'COMMUNITY_POST':
      return 'community_post' as Notification['type'];
    case 'DM':
    case 'MESSAGE':
      return 'dm' as Notification['type'];
    default:
      // Unknown/system types fall back to a generic bucket. The icon lookup
      // already degrades to a default for anything not in TYPE_ICONS.
      return (type || '').toLowerCase() as Notification['type'];
  }
}

function normalizeNotification(raw: any): Notification {
  const data = raw?.data && typeof raw.data === 'object' ? raw.data : {};
  const actorSrc = data.actor || data.user || data.fromUser || {};
  return {
    id: raw?.id,
    type: normalizeNotificationType(raw?.type),
    read: raw?.isRead ?? raw?.read ?? false,
    message: raw?.content ?? raw?.message ?? raw?.title ?? '',
    actor: {
      username: actorSrc.username ?? '',
      displayName: actorSrc.displayName ?? actorSrc.display_name ?? '',
      avatar: actorSrc.avatar ?? actorSrc.avatar_url ?? '',
    },
    postId: data.postId ?? data.post_id ?? raw?.postId,
    conversationId: data.conversationId ?? data.channelId ?? data.conversation_id,
    createdAt: raw?.createdAt ?? raw?.created_at ?? '',
  };
}

// Lists can come back as a bare array, a { data: [] } envelope, or a
// { notifications: [] } / { items: [] } envelope (Prisma relation-drift can also
// yield empty-but-200). Coerce every shape to a real array, never crash.
function coerceList(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.notifications)) return payload.notifications;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

type Section = { title: string; data: Notification[] };

// Bucket notifications into Today / This Week / Earlier (already arrives newest-first).
function groupByTime(items: Notification[]): Section[] {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfWeek = startOfToday - 6 * 24 * 60 * 60 * 1000; // last 7 days incl. today

  const today: Notification[] = [];
  const week: Notification[] = [];
  const earlier: Notification[] = [];

  for (const n of items) {
    const ts = new Date(n.createdAt).getTime();
    if (!isNaN(ts) && ts >= startOfToday) today.push(n);
    else if (!isNaN(ts) && ts >= startOfWeek) week.push(n);
    else earlier.push(n);
  }

  const sections: Section[] = [];
  if (today.length) sections.push({ title: 'Today', data: today });
  if (week.length) sections.push({ title: 'This Week', data: week });
  if (earlier.length) sections.push({ title: 'Earlier', data: earlier });
  return sections;
}

// Native CONNECT affordances (client's core ask): wallet / socials / exchange,
// all v2-gated through gateOr() so a press NEVER dead-ends (App Store 3.1.x).
// Guarded by SHOW_CRYPTO so the whole rail can be flipped off centrally.
const CONNECT_ROWS: { key: string; icon: IoniconName; title: string; sub: string }[] = [
  { key: 'wallet', icon: 'wallet-outline', title: 'Connect Wallet', sub: 'MetaMask, Coinbase & more' },
  { key: 'socials', icon: 'people-outline', title: 'Connect Socials', sub: 'X, Discord, Farcaster' },
  { key: 'exchange', icon: 'swap-horizontal-outline', title: 'Connect Exchange', sub: 'Binance, Coinbase, Kraken' },
];

function ConnectRail() {
  if (!SHOW_CRYPTO) return null;
  return (
    <GlassCard style={styles.connectCard} padding={spacing.lg}>
      <Text style={styles.connectTitle}>Connect to CRYB</Text>
      <Text style={styles.connectSubtitle}>Bring your wallet, socials and exchange in — natively.</Text>
      <View style={styles.connectRows}>
        {CONNECT_ROWS.map((r) => (
          <GlassSurface
            key={r.key}
            variant="card"
            radius={radius.lg}
            contentStyle={styles.connectRow}
            onPress={() => gateOr('Connections', () => {})}
            accessibilityLabel={`${r.title} — coming soon`}
          >
            <View style={styles.connectIconCircle}>
              <Ionicons name={r.icon} size={20} color={colors.primary} />
            </View>
            <View style={styles.connectRowText}>
              <Text style={styles.connectRowTitle}>{r.title}</Text>
              <Text style={styles.connectRowSub}>{r.sub}</Text>
            </View>
            <ComingSoonBadge subtle />
          </GlassSurface>
        ))}
      </View>
    </GlassCard>
  );
}

export default function NotificationsScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  // Trust & Safety (Apple 1.2): hide blocked users' notifications; re-render on block.
  const [blockTick, setBlockTick] = useState(0);
  useEffect(() => {
    const bump = () => setBlockTick((n) => n + 1);
    const unsubBlocked = onBlockedChange(bump);
    const unsubMuted = onMutedChange(bump);
    return () => { unsubBlocked(); unsubMuted(); };
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Re-focus refetch (skip the initial focus — mount already loads). Read-state
  // changes made elsewhere (a DM/post row marking its notification read, the app
  // badge clearing) aren't pushed to this list, so reconcile on every return.
  // Mirrors ProfileScreen's focus-refetch (skip-first-mount) pattern.
  const didInitialFocus = useRef(false);
  useFocusEffect(
    useCallback(() => {
      if (!didInitialFocus.current) { didInitialFocus.current = true; return; }
      fetchNotifications(true);
    }, []),
  );

  // Live feed: prepend new socket notifications (dedupe by id) so the feed stays
  // current without a refetch. Socket-driven, no native push needed (P4 owns push).
  useEffect(() => {
    const unsub = onNotification((payload: any) => {
      try {
        if (!payload || typeof payload !== 'object') return;
        const incoming = normalizeNotification(payload);
        if (!incoming.id) return;
        setNotifications((prev) => {
          if (prev.some((n) => n.id === incoming.id)) return prev;
          return [incoming, ...prev];
        });
      } catch {
        // ignore malformed payloads
      }
    });
    return unsub;
  }, []);

  async function fetchNotifications(isRefresh = false) {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(false);
    try {
      // Backend returns { data: notifications[] }; api() unwraps to the array,
      // but guard every envelope shape (and empty-but-200) defensively.
      const res = await api<any>('/notifications');
      if (res.success) {
        setNotifications(coerceList(res.data).map(normalizeNotification));
      } else {
        setError(true);
      }
    } catch {
      // Network error — surface a retryable error state.
      setError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function markAsRead(id: string) {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
    await api(`/notifications/${id}/read`, { method: 'POST' });
  }

  const markAllRead = useCallback(async () => {
    setNotifications(prev => {
      if (prev.every(n => n.read)) return prev;
      return prev.map(n => ({ ...n, read: true }));
    });
    await api('/notifications/read-all', { method: 'POST' });
  }, []);

  function formatTime(dateStr?: string | null): string {
    if (!dateStr) return '';
    const ts = new Date(dateStr).getTime();
    if (isNaN(ts)) return '';
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    const weeks = Math.floor(days / 7);
    return `${weeks}w`;
  }

  function handleNotificationPress(item: Notification) {
    if (!item.read) markAsRead(item.id);
    // Route by the most specific target available: a DM opens the conversation,
    // a post takes you to the post, a follow/profile-only notification takes you
    // to the actor's profile.
    if (item.type === 'dm' && item.conversationId) {
      navigation.navigate('DM', { id: item.conversationId });
    } else if (item.postId) {
      navigation.navigate('Post', { id: item.postId });
    } else if (item.actor?.username) {
      navigation.navigate('UserProfile', { username: item.actor.username });
    }
  }

  // Filter, then bucket by time. Memoized so SectionList sections stay stable.
  const sections = useMemo<Section[]>(() => {
    // Drop notifications originating from a blocked user (Apple 1.2).
    let list = notifications.filter((n) => !(isBlocked(n.actor?.username) || isMuted(n.actor?.username)));
    if (activeFilter !== 'all') {
      const typeMap: Record<string, string[]> = {
        likes: ['like'],
        comments: ['comment'],
        follows: ['follow'],
        mentions: ['mention'],
      };
      const types = typeMap[activeFilter] || [];
      list = list.filter(n => types.includes(n.type));
    }
    // NOTE: non-core types (crypto_tip / community_post / dm / repost / system)
    // have no dedicated chip by design — they surface only in "All". They are
    // never dropped from the feed itself (All applies no type filter) and never
    // counted into a per-chip badge below, so the badges stay honest.
    return groupByTime(list);
  }, [notifications, activeFilter, blockTick]);

  const unreadCount = useMemo(
    () => notifications.reduce((acc, n) => acc + (n.read ? 0 : 1), 0),
    [notifications],
  );

  // Per-filter unread counts for the pill badges.
  const filterCounts = useMemo(() => {
    const counts: Record<FilterKey, number> = { all: 0, likes: 0, comments: 0, follows: 0, mentions: 0 };
    const bucket: Record<string, FilterKey> = { like: 'likes', comment: 'comments', follow: 'follows', mention: 'mentions' };
    for (const n of notifications) {
      if (n.read) continue;
      counts.all += 1;
      const k = bucket[n.type];
      if (k) counts[k] += 1;
    }
    return counts;
  }, [notifications]);

  function renderSwipeAction() {
    return (
      <View style={styles.swipeAction}>
        <Ionicons name="checkmark-circle" size={24} color={colors.white} />
        <Text style={styles.swipeText}>Read</Text>
      </View>
    );
  }

  const renderSectionHeader = useCallback(
    ({ section }: { section: Section }) => (
      <View style={[styles.sectionHeader, containerStyle]}>
        <Text style={styles.sectionTitle}>{section.title}</Text>
      </View>
    ),
    [containerStyle],
  );

  function renderNotification({ item }: { item: Notification }) {
    const iconInfo = TYPE_ICONS[item.type] || { name: 'notifications' as IoniconName, color: colors.muted };
    const actor = item.actor || ({} as Notification['actor']);
    const actorName = actor.displayName || actor.username || 'Someone';

    const row = (
      <GlassCard
        style={[styles.notifCard, containerStyle]}
        padding={spacing.md}
        onPress={() => handleNotificationPress(item)}
        contentStyle={[styles.notifCardContent, !item.read && styles.notifCardUnread]}
        accessibilityLabel={`${actorName} ${item.message || ''}${item.read ? '' : ', unread'}`}
      >
        {!item.read && <View style={styles.notifAccent} pointerEvents="none" />}
        {/* Avatar with type badge overlay (falls back to initials, no broken image) */}
        <TouchableOpacity
          onPress={() => actor.username && navigation.navigate('UserProfile', { username: actor.username })}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel={`View ${actorName}'s profile`}
        >
          <View>
            {actor.avatar ? (
              <CachedImage source={{ uri: actor.avatar }} style={styles.notifAvatar} />
            ) : (
              <View style={[styles.notifAvatar, styles.notifAvatarFallback]}>
                <Text style={styles.notifAvatarLetter}>{actorName.charAt(0).toUpperCase()}</Text>
              </View>
            )}
            <View style={[styles.typeBadge, { backgroundColor: iconInfo.color }]}>
              <Ionicons name={iconInfo.name} size={11} color={colors.white} />
            </View>
          </View>
        </TouchableOpacity>

        {/* Content */}
        <View style={styles.notifContent}>
          <Text style={styles.notifMessage} numberOfLines={2}>
            <Text style={styles.notifActorName}>{actorName}</Text>{' '}
            {item.message || ''}
          </Text>
          <Text style={styles.notifTime}>{formatTime(item.createdAt)}</Text>
        </View>

        {/* Unread dot */}
        {!item.read && <View style={styles.unreadDot} />}
      </GlassCard>
    );

    if (!item.read) {
      return (
        <Swipeable
          renderRightActions={renderSwipeAction}
          onSwipeableOpen={() => markAsRead(item.id)}
          overshootRight={false}
        >
          {row}
        </Swipeable>
      );
    }

    return row;
  }

  if (loading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <GestureHandlerRootView style={styles.screen}>
      {/* Full-bleed animated background under the (now translucent) status bar
          and header (skill §12). */}
      <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />

      {/* In-screen translucent header (skill §12/§7): back chevron + title +
          re-homed "Mark all read". ParticleBackground shows through the notch —
          no opaque strip. paddingTop clears the status bar via safe-area inset. */}
      <GlassSurface
        variant="bar"
        mode="light"
        radius={0}
        surfaceColor="transparent"
        style={[styles.header, styles.headerTransparent]}
        contentStyle={[styles.headerContent, { paddingTop: insets.top }]}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Ionicons name="chevron-back" size={24} color={colors.ink} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Notifications</Text>
        {unreadCount > 0 ? (
          <TouchableOpacity
            onPress={markAllRead}
            activeOpacity={0.6}
            accessibilityRole="button"
            accessibilityLabel="Mark all notifications as read"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Text style={styles.markAllText}>Mark all read</Text>
          </TouchableOpacity>
        ) : (
          <View style={styles.headerRightSpacer} />
        )}
      </GlassSurface>

      <ScreenTip
        tipKey="notifications"
        title="Notifications"
        text="Your notifications. Tap to see what's new."
      />

      {/* Filter chips — the single Glass Contract pill (GlassPill) so the segmented
          filter matches Search/Explore exactly. GlassPillGroup is a content-sized
          horizontal scroller (never stretches a pill vertically), and each pill
          carries its own per-filter unread count badge. */}
      <View style={styles.chipsWrap}>
        <GlassPillGroup style={containerStyle}>
          {FILTERS.map(filter => (
            <GlassPill
              key={filter.key}
              label={filter.label}
              active={activeFilter === filter.key}
              count={filterCounts[filter.key]}
              onPress={() => setActiveFilter(filter.key)}
              accessibilityLabel={`Filter notifications by ${filter.label}`}
            />
          ))}
        </GlassPillGroup>
      </View>

      <SectionList
        sections={sections}
        keyExtractor={(item, index) => item.id || String(index)}
        renderItem={renderNotification}
        renderSectionHeader={renderSectionHeader}
        stickySectionHeadersEnabled={false}
        initialNumToRender={15}
        maxToRenderPerBatch={12}
        windowSize={11}
        removeClippedSubviews
        contentContainerStyle={[styles.listContent, sections.length === 0 && styles.listContentEmpty]}
        showsVerticalScrollIndicator={false}
        refreshing={refreshing}
        onRefresh={() => fetchNotifications(true)}
        ListHeaderComponent={
          sections.length > 0 ? (
            <View style={styles.railWrap}>
              <ConnectRail />
            </View>
          ) : null
        }
        ListEmptyComponent={
          error && notifications.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="cloud-offline-outline" size={48} color={colors.faint} />
              <Text style={styles.emptyText}>Couldn't load notifications</Text>
              <Text style={styles.emptySubtext}>
                Check your connection and try again.
              </Text>
              <TouchableOpacity
                style={styles.retryBtn}
                onPress={() => fetchNotifications()}
                activeOpacity={0.8}
                accessibilityRole="button"
                accessibilityLabel="Retry loading notifications"
              >
                <Ionicons name="refresh-outline" size={16} color="#FFFFFF" />
                <Text style={styles.retryText}>Retry</Text>
              </TouchableOpacity>
            </View>
          ) : activeFilter !== 'all' ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="funnel-outline" size={48} color={colors.faint} />
              <Text style={styles.emptyText}>Nothing here</Text>
              <Text style={styles.emptySubtext}>
                No {activeFilter} notifications yet. Try another filter.
              </Text>
            </View>
          ) : (
            <View style={styles.emptyContainer}>
              <GlassCard style={styles.emptyCard} padding={spacing.xl}>
                <Asset3D name="bell" size={128} style={{ marginBottom: spacing.md, alignSelf: 'center' }} />
                <Text style={styles.emptyText}>You're all caught up</Text>
                <Text style={styles.emptySubtext}>
                  Notifications about follows, likes and replies will show here.
                </Text>
                <GlassButton
                  title="Find people to follow"
                  icon="person-add-outline"
                  variant="secondary"
                  onPress={() => navigation.navigate('Tabs', { screen: 'Discover' })}
                  fullWidth
                  style={styles.emptyCta}
                />
              </GlassCard>
              {/* Brand-new user (the exact shown state) always gets a meaningful
                  next action instead of dead space (skill §16). */}
              <View style={styles.emptyRailWrap}>
                <ConnectRail />
              </View>
            </View>
          )
        }
      />
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.bg,
  },
  markAllText: {
    fontSize: 14,
    color: colors.primary,
    ...fonts.medium,
  },
  // --- In-screen translucent header ---
  header: {
    // Full-bleed bar; radius 0 so its bottom edge meets the chip row cleanly.
  },
  // Transparent/seamless — no white bar; ParticleBackground shows through.
  headerTransparent: {
    borderWidth: 0,
    borderBottomWidth: 0,
    shadowOpacity: 0,
    elevation: 0,
    backgroundColor: 'transparent',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
    minHeight: 44,
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    ...typographyScale.h3,
    ...fonts.semibold,
    color: colors.ink,
  },
  headerRightSpacer: {
    // Balance the back chevron so the title stays optically centered.
    width: 24,
  },
  // --- Filter chips wrapper ---
  chipsWrap: {
    // NEVER absorb flex height — keeps the horizontal pill row from stretching
    // when it sits in a flex column.
    flexGrow: 0,
    flexShrink: 0,
  },
  sectionHeader: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
  },
  sectionTitle: {
    fontSize: 13,
    color: colors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.semibold,
  },
  listContent: {
    // A12: clear A2's floating glass nav (bottomNavInset=108); was spacing.xxxl
    // (32), which hid the last notification under the bar.
    paddingBottom: layoutTokens.bottomNavInset,
    paddingTop: spacing.md,
  },
  listContentEmpty: {
    flexGrow: 1,
  },
  // Notif card now rides on the GlassCard primitive (surface + sheen + shadow);
  // these are just the outer margins + inner row layout (a token swap, no logic).
  notifCard: {
    marginHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  notifCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Unread tint layered on GlassCard: subtle primary fill (same visuals as before).
  notifCardUnread: {
    backgroundColor: colors.primary + '0D',
  },
  // Left accent bar (3px) for unread — replaces the old borderLeft on the raw View.
  notifAccent: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    backgroundColor: colors.primary,
  },
  notifAvatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: colors.faint,
    marginRight: spacing.md,
  },
  notifAvatarFallback: {
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  notifAvatarLetter: {
    color: colors.white,
    fontSize: 19,
    ...fonts.semibold,
  },
  typeBadge: {
    position: 'absolute',
    bottom: -1,
    right: spacing.md - 3,
    width: 19,
    height: 19,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.bg,
  },
  notifContent: {
    flex: 1,
  },
  notifMessage: {
    fontSize: 14,
    lineHeight: 19,
    color: colors.ink2,
    ...fonts.regular,
  },
  notifActorName: {
    color: colors.ink,
    ...fonts.semibold,
  },
  notifTime: {
    fontSize: 12,
    color: colors.faint,
    marginTop: 2,
    ...fonts.regular,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary,
    marginLeft: spacing.sm,
  },
  swipeAction: {
    backgroundColor: colors.success,
    justifyContent: 'center',
    alignItems: 'center',
    width: 80,
    paddingHorizontal: spacing.lg,
  },
  swipeText: {
    fontSize: 11,
    color: colors.white,
    marginTop: 2,
    ...fonts.medium,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 60,
    paddingHorizontal: spacing.xl,
  },
  emptyCard: {
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  emptyCta: {
    marginTop: spacing.lg,
  },
  emptyText: {
    fontSize: 17,
    color: colors.ink,
    marginTop: spacing.lg,
    ...fonts.semibold,
  },
  emptySubtext: {
    fontSize: 14,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.sm,
    lineHeight: 20,
    ...fonts.regular,
  },
  retryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 2,
    borderRadius: radius.full,
    marginTop: spacing.lg,
    gap: spacing.xs,
    minHeight: 44,
  },
  retryText: {
    fontSize: 14,
    color: '#FFFFFF',
    ...fonts.semibold,
  },
  // --- Native CONNECT rail (wallet / socials / exchange, v2-gated) ---
  railWrap: {
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  emptyRailWrap: {
    width: '100%',
    maxWidth: 340,
    marginTop: spacing.lg,
  },
  connectCard: {
    width: '100%',
  },
  connectTitle: {
    ...typographyScale.h3,
    ...fonts.semibold,
    color: colors.ink,
  },
  connectSubtitle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 2,
    marginBottom: spacing.md,
  },
  connectRows: {
    gap: spacing.sm,
  },
  connectRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    gap: spacing.md,
  },
  connectIconCircle: {
    width: 40,
    height: 40,
    borderRadius: radius.md,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
  },
  connectRowText: {
    flex: 1,
  },
  connectRowTitle: {
    fontSize: 15,
    color: colors.ink,
    ...fonts.semibold,
  },
  connectRowSub: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
  },
});
